/**
 * 2 軸集計 (マトリクス) の表示 (docs/27 §9-3)。
 *
 * 行・列とも調査項目で、セルは件数。件数が多いセルほど濃く塗る (色は列の項目の色。評価を列に
 * 選べばポジ / ネガの色になる)。0 件のグループも枠として残すので、母集団の中での偏りが分かる。
 * 表は横スクロール領域に入れる (CLAUDE.md「右が切れる」対策)。
 */
import type { AxisMatrix } from './computeAxisMatrix';

export interface StreetSurveyAxisMatrixProps {
  matrix: AxisMatrix;
  rowLabel: string;
  colLabel: string;
}

export function StreetSurveyAxisMatrix({
  matrix,
  rowLabel,
  colLabel,
}: StreetSurveyAxisMatrixProps) {
  if (matrix.rows.length === 0 || matrix.cols.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        マトリクスを作るには、行と列の調査項目をどちらも選んでください。
      </p>
    );
  }
  const max = Math.max(1, ...matrix.counts.flat());

  return (
    <div className="grid grid-cols-1 gap-1">
      <div className="overflow-x-auto rounded border border-slate-200">
        <table className="w-full min-w-[32rem] border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50">
              <th className="px-2 py-1.5 text-left font-semibold text-slate-600">
                {rowLabel} ＼ {colLabel}
              </th>
              {matrix.cols.map((c) => (
                <th key={c.key} className="px-2 py-1.5 text-center font-semibold text-slate-700">
                  <span className="inline-flex items-center gap-1">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: c.color }}
                    />
                    {c.label}
                  </span>
                </th>
              ))}
              <th className="px-2 py-1.5 text-center font-semibold text-slate-500">計</th>
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((r, ri) => (
              <tr key={r.key} className="border-t border-slate-100">
                <th className="min-w-0 px-2 py-1.5 text-left font-medium text-slate-700">
                  <span className="inline-flex items-center gap-1">
                    <span
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: r.color }}
                    />
                    <span className="truncate">{r.label}</span>
                  </span>
                </th>
                {matrix.cols.map((c, ci) => {
                  const count = matrix.counts[ri]?.[ci] ?? 0;
                  const ratio = count / max;
                  return (
                    <td
                      key={c.key}
                      className="px-2 py-1.5 text-center tabular-nums"
                      style={
                        count > 0
                          ? {
                              backgroundColor: c.color,
                              // 件数が多いほど濃く (薄い側でも色が分かる下限を残す)。
                              opacity: 0.2 + ratio * 0.8,
                              color: ratio > 0.5 ? '#ffffff' : '#0f172a',
                            }
                          : undefined
                      }
                      title={`${r.label} × ${c.label}: ${count} 件`}
                    >
                      {count > 0 ? count : <span className="text-slate-300">0</span>}
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-center font-semibold tabular-nums text-slate-700">
                  {matrix.rowTotals[ri] ?? 0}
                </td>
              </tr>
            ))}
            <tr className="border-t border-slate-200 bg-slate-50">
              <th className="px-2 py-1.5 text-left font-semibold text-slate-500">計</th>
              {matrix.cols.map((c, ci) => (
                <td
                  key={c.key}
                  className="px-2 py-1.5 text-center font-semibold tabular-nums text-slate-700"
                >
                  {matrix.colTotals[ci] ?? 0}
                </td>
              ))}
              <td className="px-2 py-1.5 text-center font-bold tabular-nums text-slate-800">
                {matrix.total}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {matrix.unresolved > 0 && (
        <p className="text-[11px] text-slate-500">
          どちらかの項目の値が無い、またはマスタで解決できない検出要素が {matrix.unresolved}{' '}
          件あり、表に含めていません。
        </p>
      )}
    </div>
  );
}
